import { useMemo } from "react";

import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { AvailabilityController, TimeTarget } from "../useAvailability";
import type { DayTimes } from "../types";
import { getTimeOptions } from "../constants";

export function TimeRanges({
  value,
  target,
  controller,
  idPrefix,
}: {
  value: DayTimes;
  target: TimeTarget;
  controller: AvailabilityController;
  idPrefix: string;
}) {
  const { setAnyTime, addRange, removeRange, updateRange } = controller;
  const { t, i18n } = useTranslation("common");
  const lng = i18n.language || "en";
  const midnightLabel = t("availabilityPicker.midnight");
  const timeOptions = useMemo(
    () => getTimeOptions(lng, midnightLabel),
    [lng, midnightLabel],
  );

  return (
    <>
      <label className="av-anytime" style={{ marginBottom: 10 }}>
        <input
          type="checkbox"
          checked={value.anyTime}
          onChange={(e) => setAnyTime(target, e.target.checked)}
        />
        {t("availabilityPicker.anyTimeWorks")}
      </label>

      <div
        className={`av-rangewrap${value.anyTime ? " is-dim" : ""}`}
        aria-hidden={value.anyTime}
      >
        {value.ranges.map((r, i) => (
          <div className="av-range" key={`${idPrefix}-${i}`}>
            <span className="av-range-lab">{t("availabilityPicker.from")}</span>
            <select
              aria-label={t("availabilityPicker.startTime")}
              value={r.startMinute}
              onChange={(e) =>
                updateRange(target, i, { startMinute: Number(e.target.value) })
              }
            >
              {timeOptions
                .filter((o) => o.value < 1440)
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
            <span className="av-range-lab">{t("availabilityPicker.to")}</span>
            <select
              aria-label={t("availabilityPicker.endTime")}
              value={r.endMinute}
              onChange={(e) =>
                updateRange(target, i, { endMinute: Number(e.target.value) })
              }
            >
              {timeOptions
                .filter((o) => o.value > 0)
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
            {value.ranges.length > 1 && (
              <button
                type="button"
                className="av-rm"
                aria-label={t("availabilityPicker.removeRange")}
                onClick={() => removeRange(target, i)}
              >
                &times;
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="av-add"
          disabled={value.anyTime}
          onClick={() => addRange(target)}
        >
          <Plus className="inline h-3 w-3 mr-1" /> {t("availabilityPicker.addRange")}
        </button>
      </div>
    </>
  );
}
